import { useEffect, useMemo, useRef, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const CATEGORIAS = {
  cobro_handy:            { label: 'Cobro Handy/Plexo',     color: '#16a34a', bg: '#d1fae5' },
  transferencia_entrante: { label: 'Transferencia recibida', color: '#16a34a', bg: '#d1fae5' },
  credito_varios:         { label: 'Crédito varios',         color: '#16a34a', bg: '#d1fae5' },
  credito_cambio:         { label: 'Crédito cambio',         color: '#0ea5e9', bg: '#e0f2fe' },
  transferencia_saliente: { label: 'Transferencia enviada',  color: '#dc2626', bg: '#fee2e2' },
  compra_tarjeta:         { label: 'Compra con débito',      color: '#dc2626', bg: '#fee2e2' },
  debito_cambio:          { label: 'Débito cambio',          color: '#7c3aed', bg: '#ede9fe' },
  retencion_iva:          { label: 'Retención IVA',          color: '#92400e', bg: '#fef3c7' },
  pago_bps:               { label: 'Pago BPS',               color: '#92400e', bg: '#fef3c7' },
  pago_dgi:               { label: 'Pago DGI/IRAE',          color: '#92400e', bg: '#fef3c7' },
  retiro_efectivo:        { label: 'Retiro efectivo',        color: '#475569', bg: '#f1f5f9' },
  saldo_apertura:         { label: 'Saldo apertura',         color: '#64748b', bg: '#e2e8f0' },
  otro:                   { label: 'Otro',                   color: '#64748b', bg: '#e2e8f0' },
}

export default function ConciliacionBancaria() {
  const fileRef = useRef()
  const [movimientos, setMovimientos] = useState([])
  const [importaciones, setImportaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [preview, setPreview] = useState(null) // null o { movimientos: [], archivo, fileObj, hash }
  const [filtroMes, setFiltroMes] = useState(() => new Date().toISOString().slice(0, 7))
  const [filtroCategoria, setFiltroCategoria] = useState('')
  const [filtroConciliado, setFiltroConciliado] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [syncingMP, setSyncingMP] = useState(false)

  useEffect(() => { cargar() }, [filtroMes])

  async function sincronizarMP() {
    setSyncingMP(true)
    try {
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mp-sincronizar-pagos`)
      const data = await r.json()
      if (!r.ok || data.error) throw new Error(data.error || 'error')
      alert(`✓ MP sincronizado
Encontrados: ${data.total_encontrados}
Insertados en movimientos: ${data.movimientos_bancarios_insertados}
Duplicados ignorados: ${data.movimientos_bancarios_skipped}`)
      await cargar()
    } catch (e) {
      alert('Error al sincronizar MP: ' + e.message)
    }
    setSyncingMP(false)
  }

  async function cargar() {
    setLoading(true)
    const desde = filtroMes ? `${filtroMes}-01` : null
    // Bug fix: junio/abr/sep/nov no tienen 31. Usamos primer día del mes siguiente con .lt()
    let hasta = null
    if (filtroMes) {
      const [_y, _m] = filtroMes.split('-').map(Number)
      hasta = new Date(_y, _m, 1).toISOString().slice(0, 10)
    }
    let q = supabase.from('movimientos_bancarios').select('*').order('fecha').order('id')
    if (desde && hasta) q = q.gte('fecha', desde).lt('fecha', hasta)
    const [{ data: movs }, { data: imps }] = await Promise.all([
      q,
      supabase.from('extractos_importaciones').select('*').order('importado_at', { ascending: false }).limit(50),
    ])
    setMovimientos(movs || [])
    setImportaciones(imps || [])
    setLoading(false)
  }

  /** Hash SHA-256 del archivo para detectar reimportaciones */
  async function sha256(buffer) {
    const hash = await crypto.subtle.digest('SHA-256', buffer)
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  async function descargarArchivo(imp) {
    const { data, error } = await supabase.storage.from('extractos-bancarios').download(imp.archivo_path)
    if (error) { alert('Error: ' + error.message); return }
    const url = URL.createObjectURL(data)
    const a = document.createElement('a')
    a.href = url
    a.download = imp.archivo_nombre
    a.click()
    URL.revokeObjectURL(url)
  }

  async function borrarImportacion(imp) {
    if (!confirm(`¿Borrar "${imp.archivo_nombre}" y sus ${imp.movimientos_insertados} movimientos? Esto borra también el archivo de Storage. No se puede deshacer.`)) return
    try {
      const { error: rpcErr } = await supabase.rpc('borrar_extracto_importacion', { p_importacion_id: imp.id })
      if (rpcErr) throw rpcErr
      // Borrar también el archivo de Storage
      await supabase.storage.from('extractos-bancarios').remove([imp.archivo_path])
      cargar()
    } catch (e) {
      alert('Error: ' + e.message)
    }
  }

  /**
   * Parsea un archivo .xls/.xlsx del extracto Itaú usando SheetJS desde CDN.
   * Estructura: R6 = headers, R7+ = movimientos.
   * Columnas: ['', 'Fecha', 'Concepto', '', 'Débito', 'Crédito', 'Saldo', 'Referencia', 'Destino']
   */
  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    try {
      const XLSX = await import('https://esm.sh/xlsx@0.18.5')
      const buf = await file.arrayBuffer()
      const hash = await sha256(buf)

      // Pre-chequeo: ¿este archivo ya se importó antes? (idempotencia por hash)
      const { data: existente } = await supabase
        .from('extractos_importaciones')
        .select('id, archivo_nombre, importado_at, movimientos_insertados')
        .eq('archivo_hash', hash)
        .maybeSingle()

      if (existente) {
        alert(
          `⚠ Este archivo ya fue importado el ${new Date(existente.importado_at).toLocaleString('es-UY')}\n\n` +
          `Archivo: ${existente.archivo_nombre}\n` +
          `Movimientos: ${existente.movimientos_insertados}\n\n` +
          `Si necesitás reimportar, primero borralo del histórico.`
        )
        setImporting(false)
        e.target.value = ''
        return
      }

      const wb = XLSX.read(buf, { type: 'array', cellDates: false })
      const sheet = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null })
      const parsed = parsearExtractoItau(rows)

      if (parsed.movimientos.length === 0) {
        alert('No se detectaron movimientos. ¿Es un Estado de Cuenta de Itaú (Excel)?')
        setImporting(false)
        e.target.value = ''
        return
      }

      setPreview({
        movimientos: parsed.movimientos,
        archivo: file.name,
        fileObj: file,
        hash,
        info: parsed.info,
      })
    } catch (err) {
      alert('Error leyendo el archivo: ' + err.message)
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  async function confirmarImportacion() {
    if (!preview) return
    setImporting(true)
    try {
      // 1. Subir archivo a Storage (path: itau/YYYY-MM/hash.xls)
      const periodoYM = preview.info?.desde?.slice(0, 7) || new Date().toISOString().slice(0, 7)
      const ext = preview.archivo.split('.').pop().toLowerCase()
      const path = `itau/${periodoYM}/${preview.hash.slice(0, 16)}.${ext}`

      const { error: upErr } = await supabase.storage
        .from('extractos-bancarios')
        .upload(path, preview.fileObj, { cacheControl: '3600', upsert: true, contentType: preview.fileObj.type })
      if (upErr) throw upErr

      // 2. Registrar importación + movimientos via RPC
      const { data, error } = await supabase.rpc('importar_movimientos_bancarios', {
        p_movimientos: preview.movimientos,
        p_archivo_nombre: preview.archivo,
        p_archivo_path: path,
        p_archivo_size_bytes: preview.fileObj.size,
        p_archivo_hash: preview.hash,
        p_banco: 'itau',
      })
      if (error) throw error

      alert(
        `✓ Importación completa\n\n` +
        `• Archivo guardado en histórico\n` +
        `• ${data.insertados} movimientos nuevos\n` +
        `• ${data.duplicados} duplicados (ya estaban en DB)\n` +
        `• Período: ${data.periodo_desde} a ${data.periodo_hasta}`
      )
      setPreview(null)
      cargar()
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setImporting(false)
    }
  }

  const filtrados = useMemo(() => {
    let f = movimientos
    if (filtroCategoria) f = f.filter(m => m.categoria_sugerida === filtroCategoria)
    if (filtroConciliado === 'conciliados') f = f.filter(m => m.conciliado)
    if (filtroConciliado === 'pendientes') f = f.filter(m => !m.conciliado)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(m =>
        (m.concepto || '').toLowerCase().includes(q) ||
        (m.referencia || '').toLowerCase().includes(q)
      )
    }
    return f
  }, [movimientos, filtroCategoria, filtroConciliado, busqueda])

  const kpis = useMemo(() => {
    let totalDeb = 0, totalCre = 0, conciliados = 0, pendientes = 0
    for (const m of filtrados) {
      totalDeb += Number(m.debito || 0)
      totalCre += Number(m.credito || 0)
      if (m.conciliado) conciliados++
      else pendientes++
    }
    return { total: filtrados.length, totalDeb, totalCre, conciliados, pendientes, neto: totalCre - totalDeb }
  }, [filtrados])

  const categoriasEnDatos = useMemo(() => {
    const set = new Set()
    movimientos.forEach(m => m.categoria_sugerida && set.add(m.categoria_sugerida))
    return Array.from(set).sort()
  }, [movimientos])

  return (
    <AdminLayout>
      <div className="cb-wrap">
        <div className="cb-headline">
          <div>
            <h1>Conciliación MP</h1>
            <p>Movimientos MercadoPago sincronizados automáticamente cada 6hs. Conciliá contra los asientos contables.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cb-btn-primary" onClick={sincronizarMP} disabled={syncingMP} type="button">
              {syncingMP ? '⏳ Sincronizando…' : '🔄 Sincronizar MP'}
            </button>
            <label className="cb-btn-primary" style={{ background: '#F1F5F9', color: '#475569' }}>
              {importing ? 'Procesando…' : '📂 Excel (banco físico)'}
              <input ref={fileRef} type="file" accept=".xls,.xlsx" onChange={handleFile} disabled={importing} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        {/* Banner instrucciones */}
        <div className="cb-instructions">
          <strong>📋 Cómo funciona:</strong>
          <ol>
            <li>Limpack solo opera con cuenta <strong>MP LIMPACK SAS (nickname LIMPACKUY)</strong>. Los movimientos entran automáticamente cada 6hs desde MercadoPago.</li>
            <li>Podés forzar la sincronización tocando <strong>"🔄 Sincronizar MP"</strong> arriba a la derecha.</li>
            <li>Cada pago aparece con canal (tienda / suscripción / ML / warmup) y categoría auto-sugerida.</li>
            <li>Andá a <a href="/admin/finanzas/conciliar-banco" style={{ color: '#0891b2', fontWeight: 700 }}>Conciliar banco ↔ asientos</a> para aprobar los matches con los asientos Zeta.</li>
            <li>Si en el futuro abren cuenta bancaria física (Itaú/BROU), el botón "📂 Excel (banco físico)" sigue funcionando para importar extractos manuales.</li>
          </ol>
        </div>

        {/* KPIs */}
        <div className="cb-kpis">
          <KpiCard label="Movimientos del mes" value={kpis.total} sub="filtrados" />
          <KpiCard label="Créditos (entradas)" value={'$' + fmt(kpis.totalCre)} sub="" color="#16a34a" />
          <KpiCard label="Débitos (salidas)" value={'$' + fmt(kpis.totalDeb)} sub="" color="#dc2626" />
          <KpiCard label="Neto" value={'$' + fmt(kpis.neto)} sub="entradas − salidas" color={kpis.neto >= 0 ? '#16a34a' : '#dc2626'} />
          <KpiCard label="Conciliados / pendientes" value={`${kpis.conciliados} / ${kpis.pendientes}`} sub="" color="#7c3aed" />
        </div>

        {/* Filtros */}
        <div className="cb-toolbar">
          <label className="cb-fil">
            <span>Mes</span>
            <input type="month" value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)} />
          </label>
          <label className="cb-fil">
            <span>Categoría</span>
            <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
              <option value="">Todas</option>
              {categoriasEnDatos.map(c => <option key={c} value={c}>{CATEGORIAS[c]?.label || c}</option>)}
            </select>
          </label>
          <label className="cb-fil">
            <span>Estado</span>
            <select value={filtroConciliado} onChange={(e) => setFiltroConciliado(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="pendientes">Pendientes de conciliar</option>
              <option value="conciliados">Ya conciliados</option>
            </select>
          </label>
          <label className="cb-fil">
            <span>Buscar</span>
            <input type="search" placeholder="Concepto o referencia…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </label>
          <div className="cb-toolbar-info">{filtrados.length} movimientos</div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="cb-loading">Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div className="cb-empty">
            {movimientos.length === 0
              ? <><strong>📭 No hay movimientos cargados todavía.</strong><p>Subí el primer extracto Itaú para arrancar.</p></>
              : <strong>Sin movimientos en este filtro.</strong>}
          </div>
        ) : (
          <div className="cb-tabla-wrap">
            <table className="cb-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Referencia</th>
                  <th>Categoría</th>
                  <th style={{ textAlign: 'right' }}>Débito</th>
                  <th style={{ textAlign: 'right' }}>Crédito</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(m => {
                  const cat = CATEGORIAS[m.categoria_sugerida] || CATEGORIAS.otro
                  return (
                    <tr key={m.id} className={m.conciliado ? 'cb-row-conciliado' : ''}>
                      <td style={{ whiteSpace: 'nowrap' }}>{m.fecha}</td>
                      <td>{m.concepto}</td>
                      <td style={{ fontSize: 11, color: '#64748b' }}>{m.referencia || ''}</td>
                      <td>
                        <span className="cb-pill-cat" style={{ background: cat.bg, color: cat.color }}>
                          {cat.label}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>{Number(m.debito) > 0 ? '$' + fmt(m.debito) : '—'}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a' }}>{Number(m.credito) > 0 ? '$' + fmt(m.credito) : '—'}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.saldo_post ? '$' + fmt(m.saldo_post) : '—'}</td>
                      <td>{m.conciliado ? <span className="cb-pill-ok">✓ Conciliado</span> : <span className="cb-pill-pend">Pendiente</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Histórico de importaciones */}
        <h2 className="cb-h2">📁 Histórico de extractos importados</h2>
        {importaciones.length === 0 ? (
          <div className="cb-empty">
            <strong>Sin importaciones todavía.</strong>
            <p>Cada vez que subas un extracto, queda registrado acá con el archivo original para auditoría.</p>
          </div>
        ) : (
          <div className="cb-tabla-wrap">
            <table className="cb-tabla">
              <thead>
                <tr>
                  <th>Importado</th>
                  <th>Archivo</th>
                  <th>Período cubierto</th>
                  <th style={{ textAlign: 'right' }}>Filas</th>
                  <th style={{ textAlign: 'right' }}>Nuevos</th>
                  <th style={{ textAlign: 'right' }}>Duplicados</th>
                  <th style={{ textAlign: 'right' }}>Tamaño</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {importaciones.map(imp => (
                  <tr key={imp.id}>
                    <td style={{ fontSize: 12 }}>{new Date(imp.importado_at).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td style={{ fontWeight: 700 }}>{imp.archivo_nombre}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>
                      {imp.periodo_desde} → {imp.periodo_hasta}
                    </td>
                    <td style={{ textAlign: 'right' }}>{imp.total_filas}</td>
                    <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 700 }}>{imp.movimientos_insertados}</td>
                    <td style={{ textAlign: 'right', color: '#94a3b8' }}>{imp.movimientos_duplicados}</td>
                    <td style={{ textAlign: 'right', fontSize: 11, color: '#64748b' }}>
                      {Math.round(imp.archivo_size_bytes / 1024)} KB
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="cb-btn-ghost" onClick={() => descargarArchivo(imp)} title="Descargar archivo original">⬇ Descargar</button>
                      <button className="cb-btn-del" onClick={() => borrarImportacion(imp)} title="Borrar importación + archivo">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info */}
        <div className="cb-info">
          <strong>🔍 Categorización automática</strong>
          <p style={{ fontSize: 13, color: '#334155' }}>
            Limpack opera <b>100% en MercadoPago</b> (sin cuenta bancaria). Todo movimiento entra o sale por acá.
          </p>

          <p style={{ marginTop: 10 }}><b>💚 Ingresos</b> (por <code>external_reference</code> del cobro MP):</p>
          <ul>
            <li><b>venta_tienda</b> → Cobros de la tienda pública (<code>pedido:UUID</code>)</li>
            <li><b>cobro_suscripcion</b> → Suscripciones mensuales (<code>pago:UUID</code> / <code>cliente:UUID</code>)</li>
            <li><b>venta_ml</b> → Ventas Mercado Libre (<code>ml:XXXX</code>)</li>
            <li><b>ingreso_test</b> → Warmup / pruebas técnicas (<code>warmup:...</code>)</li>
            <li><b>devolucion_iva</b> → Créditos DGI (REDIVA)</li>
            <li><b>ingreso_varios</b> → Otros ingresos sin referencia</li>
          </ul>

          <p style={{ marginTop: 10 }}><b>🔻 Egresos</b> (por descripción del pago MP):</p>
          <ul>
            <li><b>compra_proveedor</b> → Pagos a proveedores (NEBA, Plaza Inca, Boyerco, Cottonmax…)</li>
            <li><b>impuesto_dgi</b> → IVA / IRAE / anticipos DGI</li>
            <li><b>impuesto_bps</b> → Aportes BPS</li>
            <li><b>comision_mp</b> → Comisión de la propia plataforma (gasto)</li>
            <li><b>retiro_socio</b> → Retiros / distribución de utilidades</li>
            <li><b>egreso_varios</b> → Cualquier otro egreso sin categoría clara</li>
          </ul>

          <p style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
            <strong>Próximo paso:</strong> matching automático entre cada movimiento y los asientos contables pendientes. Roberto aprueba en <a href="/admin/finanzas/conciliar-banco" style={{ color: '#0891b2', fontWeight: 700 }}>Conciliar banco ↔ asientos</a>.
          </p>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
            <strong>Comisión MP:</strong> variable según medio de pago (0.99% dinero MP · 5.99% tarjeta crédito · similar prepago). Se registra automáticamente como <b>comision_mp</b> separada del ingreso bruto.
          </p>
        </div>
      </div>

      {/* Modal preview antes de confirmar importación */}
      {preview && (
        <div className="cb-modal-bg" onClick={() => setPreview(null)}>
          <div className="cb-modal" onClick={(e) => e.stopPropagation()}>
            <button className="cb-modal-close" onClick={() => setPreview(null)}>×</button>
            <h2>Preview de importación</h2>
            <p className="cb-modal-sub">
              Archivo: <b>{preview.archivo}</b> · {preview.movimientos.length} movimientos detectados
              {preview.info && <> · Período: <b>{preview.info.desde}</b> a <b>{preview.info.hasta}</b></>}
            </p>

            <div className="cb-preview-tabla-wrap">
              <table className="cb-preview-tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Referencia</th>
                    <th style={{ textAlign: 'right' }}>Débito</th>
                    <th style={{ textAlign: 'right' }}>Crédito</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.movimientos.slice(0, 8).map((m, i) => (
                    <tr key={i}>
                      <td>{m.fecha}</td>
                      <td>{m.concepto}</td>
                      <td style={{ fontSize: 11, color: '#64748b' }}>{m.referencia || ''}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>{Number(m.debito) > 0 ? '$' + fmt(m.debito) : '—'}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a' }}>{Number(m.credito) > 0 ? '$' + fmt(m.credito) : '—'}</td>
                    </tr>
                  ))}
                  {preview.movimientos.length > 8 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', padding: 10 }}>… y {preview.movimientos.length - 8} más</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="cb-modal-actions">
              <button className="cb-btn-secondary" onClick={() => setPreview(null)}>Cancelar</button>
              <button className="cb-btn-primary" onClick={confirmarImportacion} disabled={importing}>
                {importing ? 'Importando…' : `Confirmar e importar ${preview.movimientos.length} movimientos`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

/**
 * Parser específico para el formato del Estado de Cuenta de Itaú (Excel).
 * Estructura detectada en el ejemplo del 05-jun-2026:
 *   R0-R5: metadatos (titular, cuenta)
 *   R6: headers ['', 'Fecha', 'Concepto', '', 'Débito', 'Crédito', 'Saldo', 'Referencia', 'Destino']
 *   R7+: movimientos (R7 suele ser "SALDO ANTERIOR")
 */
function parsearExtractoItau(rows) {
  const movimientos = []
  let desde = null
  let hasta = null

  // Buscar la fila de headers
  let headerRowIdx = -1
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const row = rows[i] || []
    if (row.some(v => String(v || '').toLowerCase() === 'fecha') &&
        row.some(v => String(v || '').toLowerCase() === 'concepto')) {
      headerRowIdx = i
      break
    }
  }
  if (headerRowIdx === -1) {
    throw new Error('No encontré la fila de headers (Fecha / Concepto). ¿Es un Estado de Cuenta de Itaú?')
  }

  // Detectar índices de columnas mirando el header
  const header = rows[headerRowIdx].map(v => String(v || '').toLowerCase())
  const idxFecha    = header.findIndex(v => v === 'fecha')
  const idxConcepto = header.findIndex(v => v === 'concepto')
  const idxDebito   = header.findIndex(v => v === 'débito' || v === 'debito')
  const idxCredito  = header.findIndex(v => v === 'crédito' || v === 'credito')
  const idxSaldo    = header.findIndex(v => v === 'saldo')
  const idxRef      = header.findIndex(v => v === 'referencia')

  // Procesar filas de datos (desde headerRowIdx + 1)
  for (let i = headerRowIdx + 1; i < rows.length; i++) {
    const row = rows[i] || []
    const fecha = row[idxFecha]
    const concepto = String(row[idxConcepto] || '').trim()
    if (!fecha || !concepto) continue

    // Parsear fecha DD/MM/YYYY a YYYY-MM-DD
    const fechaISO = parseFechaItau(fecha)
    if (!fechaISO) continue
    if (!desde || fechaISO < desde) desde = fechaISO
    if (!hasta || fechaISO > hasta) hasta = fechaISO

    // Skip "SALDO ANTERIOR" (es informativo, no es un movimiento real)
    if (concepto.toUpperCase().startsWith('SALDO ANTERIOR')) continue

    const debito = parseNumero(row[idxDebito])
    const credito = parseNumero(row[idxCredito])
    const saldo = parseNumero(row[idxSaldo])
    const referencia = String(row[idxRef] || '').trim() || null

    // Si no hay débito ni crédito, no es un movimiento útil
    if (debito === 0 && credito === 0) continue

    movimientos.push({
      fecha: fechaISO,
      concepto,
      referencia,
      debito,
      credito,
      saldo_post: saldo,
    })
  }

  return { movimientos, info: { desde, hasta } }
}

function parseFechaItau(v) {
  if (!v) return null
  // Itaú formatea DD/MM/YYYY como string
  if (typeof v === 'string') {
    const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (m) return `${m[3]}-${m[2]}-${m[1]}`
  }
  // Por si viene como Date object
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  // Por si viene como número serial Excel
  if (typeof v === 'number' && v > 0) {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000))
    return d.toISOString().slice(0, 10)
  }
  return null
}

function parseNumero(v) {
  if (v == null || v === '') return 0
  if (typeof v === 'number') return v
  // String con coma decimal y punto separador de miles
  if (typeof v === 'string') {
    const limpio = v.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '')
    const n = parseFloat(limpio)
    return isNaN(n) ? 0 : n
  }
  return 0
}

function KpiCard({ label, value, sub, color = '#1A3461' }) {
  return (
    <div className="cb-kpi">
      <div className="cb-kpi-l">{label}</div>
      <div className="cb-kpi-v" style={{ color }}>{value}</div>
      {sub && <div className="cb-kpi-s">{sub}</div>}
    </div>
  )
}

const CSS = `
.cb-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.cb-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 16px }
.cb-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.cb-headline p { font-size: 13px; color: #64748b; margin: 0 }

.cb-btn-primary { padding: 9px 18px; background: #1A3461; color: #fff; border: 0; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit; display: inline-block }
.cb-btn-primary:hover { background: #0f2147 }
.cb-btn-primary:disabled { opacity: .6; cursor: not-allowed }
.cb-btn-secondary { padding: 9px 18px; background: #fff; border: 1px solid #cbd5e1; color: #475569; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.cb-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer; margin-right: 4px; font-family: inherit }
.cb-btn-ghost:hover { background: #f1f5f9; border-color: #94a3b8 }
.cb-btn-del { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 4px 9px; border-radius: 5px; font-weight: 800; cursor: pointer; font-family: inherit }
.cb-btn-del:hover { background: #fee2e2 }
.cb-h2 { font-size: 16px; font-weight: 900; color: #1A3461; margin: 28px 0 12px }

.cb-instructions { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 12px 16px; margin-bottom: 14px; font-size: 12.5px; color: #1e3a8a }
.cb-instructions strong { font-size: 13px }
.cb-instructions ol { margin: 6px 0 0 20px; padding: 0 }
.cb-instructions li { margin-bottom: 3px }

.cb-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; margin-bottom: 14px }
.cb-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.cb-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.cb-kpi-v { font-size: 20px; font-weight: 900 }
.cb-kpi-s { font-size: 11px; color: #64748b }

.cb-toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end; margin-bottom: 12px }
.cb-fil { display: flex; flex-direction: column; gap: 3px }
.cb-fil span { font-size: 11px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.cb-fil input, .cb-fil select { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit; color: #1A3461; min-width: 160px }
.cb-toolbar-info { margin-left: auto; font-size: 12px; color: #64748b }

.cb-loading, .cb-empty { padding: 50px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.cb-empty strong { display: block; color: #1A3461; font-size: 14px; margin-bottom: 6px }
.cb-empty p { font-size: 13px; color: #64748b; margin: 4px 0 0 }

.cb-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.cb-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 1100px }
.cb-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0 }
.cb-tabla td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.cb-tabla tr:hover { background: #f8fafc }
.cb-row-conciliado { background: #f0fdf4 }
.cb-row-conciliado:hover { background: #dcfce7 }

.cb-pill-cat { display: inline-block; padding: 3px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap }
.cb-pill-ok { display: inline-block; padding: 3px 8px; background: #d1fae5; color: #065f46; border-radius: 5px; font-size: 10.5px; font-weight: 800 }
.cb-pill-pend { display: inline-block; padding: 3px 8px; background: #fef3c7; color: #92400e; border-radius: 5px; font-size: 10.5px; font-weight: 800 }

.cb-info { margin-top: 20px; padding: 14px 18px; background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 10px; font-size: 12.5px; color: #115e59; line-height: 1.6 }
.cb-info strong { font-size: 13px; color: #0f4c4a }
.cb-info ul { margin: 8px 0; padding-left: 22px }
.cb-info b { color: #134e4a }

/* Modal */
.cb-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.cb-modal { background: #fff; border-radius: 14px; max-width: 920px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative }
.cb-modal h2 { margin: 0 0 6px; font-size: 20px; color: #1A3461; font-weight: 900 }
.cb-modal-sub { font-size: 12.5px; color: #64748b; margin: 0 0 14px }
.cb-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }
.cb-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; padding-top: 14px; border-top: 1px solid #e2e8f0 }

.cb-preview-tabla-wrap { max-height: 380px; overflow: auto; border: 1px solid #e2e8f0; border-radius: 8px }
.cb-preview-tabla { width: 100%; border-collapse: collapse; font-size: 12px }
.cb-preview-tabla th { text-align: left; padding: 8px 10px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0 }
.cb-preview-tabla td { padding: 7px 10px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
`
